import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { toast } from 'sonner';
import type { ApiResponse } from '@/types/api';
import { clearSession, getAuthToken } from '@/stores/userStore';
import { cancelManager } from '.';
import type { IRequestConfig } from '.';
import { APP_CONFIG } from '@/config/env';
import { addMsgOnReloaded } from '@/utils/msgOnReload';
import { ApiBusinessError, parseUserError } from '@/utils/userError';

/** 仅解析提示信息，不替换 Axios 原始异常，以保留调用方的传输错误和取消判断 */
const parseRequestError = (error: unknown, config?: IRequestConfig) =>
  parseUserError(error, {
    context: config?.errorContext,
    online: typeof navigator === 'undefined' ? undefined : navigator.onLine,
  });

/** 默认由请求层提示；页面接管时只关闭提示，不吞掉错误或改变认证流程 */
const notifyRequestError = (message: string | undefined, config?: IRequestConfig) => {
  if (message && config?.showErrorToast !== false) {
    toast.error(message);
  }
};

/** 无配置的运行异常交由请求入口提示，才能保留页面传入的提示责任及操作上下文 */
export const notifyUnconfiguredRequestError = (error: unknown, config?: IRequestConfig) => {
  if (
    error instanceof ApiBusinessError ||
    (typeof error === 'object' && error !== null && 'config' in error && error.config)
  )
    return;
  const parsed = parseRequestError(error, config);
  if (parsed.kind === 'canceled') return;
  notifyRequestError(parsed.message, config);
  console.error('请求执行失败:', error);
};

/**
 * 请求拦截器
 */
export const setupRequestInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getAuthToken();
      // 如果存在 token,添加到请求头
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error: AxiosError) => {
      console.error('请求错误:', error);
      return Promise.reject(error);
    }
  );
};

/**
 * 响应拦截器
 */
export const setupResponseInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      cancelManager.removeRequest(response.config);

      // 如果响应是二进制流（Blob/ArrayBuffer），直接返回，因为导出只会回传文件流
      if (response.data instanceof Blob || response.data instanceof ArrayBuffer) {
        return response;
      }

      const { code, msg } = response.data;
      if (code === 0) {
        return response;
      }

      const requestConfig: IRequestConfig = response.config;
      const parsed = parseRequestError(response.data, requestConfig);
      const userMessage =
        code === 401 ? '登录状态已失效，请重新登录' : parsed.message || '请求失败，请稍后重试';
      // 保留完整响应作为 cause，业务文案进入 message，调用方仍可读取原始 code/msg
      const businessError = new ApiBusinessError({ code, msg }, userMessage, { cause: response });
      // 主动退出的结果由调用方统一提示，避免出现冲突或重复的 toast
      if (response.config.url === '/auth/logout' && code !== 0) {
        return Promise.reject(businessError);
      }
      if (code === 401) {
        if (!APP_CONFIG.isDev) {
          // 清除用户信息（用于退出登录）
          clearSession();
          // 页面接管仅关闭通知，不能跳过清理会话和原有跳转
          if (requestConfig.showErrorToast !== false) {
            addMsgOnReloaded({ type: 'error', content: userMessage });
          }
          window.location.hash = '/login';
          window.location.reload();
        } else {
          console.warn(`接口返回${code},非dev环境会退出登录`);
        }
        return Promise.reject(businessError);
      }
      // 业务错误
      notifyRequestError(userMessage, response.config);
      return Promise.reject(businessError);
    },
    (error: AxiosError<ApiResponse>) => {
      if (error.config) {
        cancelManager.removeRequest(error.config);
        if (APP_CONFIG.isDev) {
          console.log(
            `[cancelManager]移除已${error.code === 'ERR_CANCELED' ? '取消' : '失败'}的请求：${error.config.headers?.['Request-Key']}`
          );
        }
      }
      // 登出失败仍由主动退出流程清理本地会话并提示结果
      if (error.config?.url === '/auth/logout') {
        return Promise.reject(error);
      }
      const parsed = parseRequestError(error, error.config);
      if (parsed.kind === 'canceled') {
        return Promise.reject(error);
      }
      // 丢失配置时由请求入口保留提示责任，HTTP 401 仍沿用既有会话清理
      if (!error.config) {
        if (error.response?.status === 401) clearSession();
        return Promise.reject(error);
      }

      if (error.response?.status === 401) {
        // HTTP 未授权沿用既有清理流程，关闭 Toast 不会关闭认证处理
        clearSession();
      }
      notifyRequestError(parsed.message, error.config);
      console.error('响应错误:', error);

      return Promise.reject(error);
    }
  );
};

/**
 * 设置所有拦截器
 */
export const setupInterceptors = (instance: AxiosInstance) => {
  setupRequestInterceptor(instance);
  setupResponseInterceptor(instance);
};
